import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { useAuth0 } from '../../__mocks__/@auth0/auth0-react';
import { REACT_APP_BASE_URL_WEBSOCKET } from '../../utils/envVar';

export type WebSocketStatus = 'connected' | 'reconnecting' | 'disconnected';

type WebSocketContextType = {
  subscribe: (channel: string, callback: (data: any) => void) => () => void;
  sendMessage: (message: any) => void;
  status: WebSocketStatus;
  lastConnectedAt: number | null;
};

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, getIdTokenClaims, getAccessTokenSilently } =
    useAuth0();
  const wsRef = useRef<WebSocket | null>(null);
  const subscribersRef = useRef<Map<string, Set<(data: any) => void>>>(
    new Map(),
  );
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout>();
  const lastHeartbeatRef = useRef<number>(Date.now());
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const [lastConnectedAt, setLastConnectedAt] = useState<number | null>(null);

  const RECONNECT_INTERVAL = 5000;
  const HEARTBEAT_TIMEOUT = 20000;

  const sendMessageToSentry = () => {
    Sentry.captureMessage('WebSocket connection closed unexpectedly', {
      level: 'error',
      extra: {
        timestamp: new Date().toUTCString(),
      },
    });
  };

  const checkHeartbeat = () => {
    const now = Date.now();
    if (now - lastHeartbeatRef.current > HEARTBEAT_TIMEOUT) {
      try {
        wsRef.current?.send(
          JSON.stringify({
            channel: 'ping',
            data: {
              timestamp: new Date().toISOString(),
            },
          }),
        );

        setTimeout(() => {
          if (
            Date.now() - lastHeartbeatRef.current >
            HEARTBEAT_TIMEOUT + RECONNECT_INTERVAL
          ) {
            if (wsRef.current) {
              wsRef.current.onclose = null;
              wsRef.current.close();
              sendMessageToSentry();
              connect();
            }
          }
        }, RECONNECT_INTERVAL);
      } catch (error) {
        if (wsRef.current) {
          wsRef.current.onclose = null;
          wsRef.current.close();
          sendMessageToSentry();
          connect();
        }
      }
    }
  };

  const connect = async () => {
    try {
      setStatus('reconnecting');
      if (heartbeatTimeoutRef.current) {
        clearInterval(heartbeatTimeoutRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Renew before (re)connecting — reconnects can happen long after the
      // cached ID token expired.
      try {
        await getAccessTokenSilently();
      } catch {
        // fall through with cached claims; server will reject if truly dead
      }
      const token = await getIdTokenClaims();

      if (wsRef.current) {
        wsRef.current.close();
      }

      wsRef.current = new WebSocket(
        `${REACT_APP_BASE_URL_WEBSOCKET}?bearer=${token?.__raw}`,
      );

      wsRef.current.onopen = () => {
        lastHeartbeatRef.current = Date.now();
        setStatus('connected');
        setLastConnectedAt(Date.now());
        heartbeatTimeoutRef.current = setInterval(
          checkHeartbeat,
          RECONNECT_INTERVAL,
        );
      };

      wsRef.current.onmessage = event => {
        const data = JSON.parse(event.data);
        if (data.channel === 'heartbeat' || data.channel === 'pong') {
          lastHeartbeatRef.current = Date.now();
          return;
        }

        const subscribers = subscribersRef.current.get(data.channel);
        if (subscribers) {
          subscribers.forEach(callback => callback(data));
        }
      };

      wsRef.current.onclose = () => {
        if (heartbeatTimeoutRef.current) {
          clearInterval(heartbeatTimeoutRef.current);
        }

        if (wsRef.current?.onclose) {
          setStatus('reconnecting');
          reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_INTERVAL);
        } else {
          setStatus('disconnected');
        }
      };

      wsRef.current.onerror = () => {
        wsRef.current?.close();
      };
    } catch (error) {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_INTERVAL);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      connect();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (heartbeatTimeoutRef.current) {
        clearInterval(heartbeatTimeoutRef.current);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const subscribe = useCallback(
    (channel: string, callback: (data: any) => void) => {
      if (!subscribersRef.current.has(channel)) {
        subscribersRef.current.set(channel, new Set());
      }
      subscribersRef.current.get(channel)?.add(callback);

      return () => {
        subscribersRef.current.get(channel)?.delete(callback);
        if (subscribersRef.current.get(channel)?.size === 0) {
          subscribersRef.current.delete(channel);
        }
      };
    },
    [],
  );

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const value = useMemo(
    () => ({ subscribe, sendMessage, status, lastConnectedAt }),
    [subscribe, sendMessage, status, lastConnectedAt],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
